import { Checkbox, Intent } from "@blueprintjs/core";
import { ButtonContainer, IDataTableColumn, showError, showSuccess } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";

import {
  CompetitionRequestApi,
  CompetitionRequestGridDto,
  CompetitionRequestSearch,
  ERequestState,
} from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { AddButton, EditButton, SearchTablePage, ViewButton, CloseButton } from "../../../../components";
import { useDialog, useGlobalData, useEventsContext } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";

export interface ICompetitionRequestListProps {}

export const CompetitionRequestList: React.FunctionComponent<ICompetitionRequestListProps> = (props) => {
  const api = useApiService(CompetitionRequestApi);
  const { t } = useTl();
  const history = useHistory();
  const { currentLanguage } = useGlobalData();
  const { dispatchEvent } = useEventsContext();
  const { showDialog } = useDialog();

  const selectionList = React.useMemo(() => [], []);
  const closableIdsList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);

  //===================== Display ==============================
  const columnCheckBox = React.useCallback(
    (row: CompetitionRequestGridDto) => {
      const disabled = row.requestStateCode !== ERequestState.PAID;
      if (!disabled) {
        console.log("closable: ", row.requestId);
        closableIdsList.push(row.requestId);
      }
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.requestId)}
          onChange={() => {
            if (selectionList.includes(row.requestId)) {
              const index = selectionList.findIndex((e) => e === row.requestId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.requestId);
            }
          }}
          disabled={disabled}
        />
      );
    },
    [closableIdsList, selectionList]
  );

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: CompetitionRequestGridDto) => (
          <ButtonContainer>
            <ViewButton
              minimal={true}
              onClick={() => history.push(`${ERoutes.competitionRequest}/${row.competitionRequestId}/detail/view`)}
            />
            <EditButton
              minimal={true}
              disabled={row.requestStateCode === ERequestState.EXECUTED}
              onClick={() => history.push(`${ERoutes.competitionRequest}/${row.competitionRequestId}/detail/edit`)}
            />
          </ButtonContainer>
        ),
      },
      {
        header: () => t(ETLCodes.RequestBlocked),
        fieldName: "isBlocked",
        alignment: "center",
        render: (row: CompetitionRequestGridDto) => <Checkbox checked={row.isBlocked} disabled />,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
      {
        header: () => t(ETLCodes.DateFrom),
        fieldName: "dateFrom",
      },
      {
        header: () => t(ETLCodes.DateTo),
        fieldName: "dateTo",
      },
      {
        header: () => t(ETLCodes.Contact),
        fieldName: "contact",
        render: (row: CompetitionRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.contactId}/detail/view`}>
            {row.contact}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Club),
        fieldName: "clubName",
        render: (row: CompetitionRequestGridDto) => (
          <Link target="_blank" to={`${ERoutes.club}/${row.clubId}/detail/view`}>
            {row.clubName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Invoice),
        fieldName: "invoiceNumber",
      },
      {
        header: () => t(ETLCodes.MyDogShow),
        fieldName: "myDogShow",
        render: (row: CompetitionRequestGridDto) => <Checkbox checked={row.myDogShow} disabled />,
      },
      {
        header: () => t(ETLCodes.MyDogShowActionRequired),
        fieldName: "myDogShowActionRequired",
        render: (row: CompetitionRequestGridDto) => <Checkbox checked={row.myDogShowActionRequired} disabled />,
      },
    ],
    [columnCheckBox, history, t, toggleSelectAll]
  );

  //===================== Functions ==============================
  const closeRequests = React.useCallback(
    (requestList) => {
      if (selectionList.length === 0) {
        showDialog({
          message: t(ETLCodes.CloseRequestSelectionEmpty),
          title: t(ETLCodes.CloseRequests),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.CloseRequestConfirmationMessage),
          title: t(ETLCodes.CloseRequests),
          onConfirmed: async () => {
            try {
              requestList.forEach(async (element, index) => {
                await api.close(element);
                if (index === requestList?.length - 1) {
                  dispatchEvent("RefreshSearchTables", 0);
                }
              });
              selectionList.splice(0, selectionList.length);
              showSuccess(t(ETLCodes.RequestsClosedSuccess));
            } catch (error) {
              showError(t(ETLCodes.Error));
            }
            dispatchEvent("RefreshSearchTables", 0);
          },
        });
      }
    },
    [api, dispatchEvent, selectionList, showDialog, t]
  );

  const addItemFunction = React.useCallback(() => {
    history.push(`${ERoutes.competitionRequest}/0/detail/edit`);
  }, [history]);
  const search = React.useCallback(
    (sObj) =>
      api.baseSearch(
        CompetitionRequestSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [api, currentLanguage.languageId]
  );

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={() => api.getSearchCriterias(true)}
      searchFunction={search}
      exportFunction={(sObj) =>
        api.exportXlsFile(
          CompetitionRequestSearch.fromJS({
            ...sObj,
            languageId: currentLanguage.languageId,
          })
        )
      }
      sortKeys={{ dateFrom: "DESC" }}
      breadCrumbs={[{ text: t(ETLCodes.CompetitionRequests) }]}
      rightElement={
        <div>
          <AddButton text={t(ETLCodes.General_Add)} onClick={() => addItemFunction()} />
          <CloseButton text={t(ETLCodes.Close)} onClick={() => closeRequests(selectionList)} />
        </div>
      }
    />
  );
};
